
import { useEffect, useState } from 'react';

interface AnimatedCodeBackgroundProps {
  opacity?: number;
  className?: string;
}

export default function AnimatedCodeBackground({ 
  opacity = 0.1, 
  className = "" 
}: AnimatedCodeBackgroundProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} style={{ opacity }}>
      {/* 第一層程式碼流 */}
      <div className="absolute top-10 left-0 w-full animate-pulse">
        <div className="flex space-x-8 animate-bounce">
          <span className="text-pink-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300 animate-pulse" style={{
            textShadow: '0 0 10px rgba(236, 72, 153, 0.5), 0 0 20px rgba(236, 72, 153, 0.3)',
            animation: 'rainbow-text 3s ease-in-out infinite, bounce 2s ease-in-out infinite'
          }}>💖 function love() &#123; return happiness; &#125;</span>
          <span className="text-blue-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300 animate-pulse delay-200" style={{
            textShadow: '0 0 10px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3)',
            animation: 'rainbow-text 3s ease-in-out infinite 0.5s, bounce 2s ease-in-out infinite 0.3s'
          }}>🌈 const rainbow = [&apos;red&apos;, &apos;orange&apos;, &apos;yellow&apos;];</span>
          <span className="text-green-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300 animate-pulse delay-500" style={{
            textShadow: '0 0 10px rgba(34, 197, 94, 0.5), 0 0 20px rgba(34, 197, 94, 0.3)',
            animation: 'rainbow-text 3s ease-in-out infinite 1s, bounce 2s ease-in-out infinite 0.7s'
          }}>✨ if (smile) &#123; world.brighten(); &#125;</span>
        </div>
      </div>

      {/* 第二層程式碼流 */}
      <div className="absolute top-32 right-0 w-full animate-pulse delay-300">
        <div className="flex space-x-6 animate-bounce delay-500">
          <span className="text-purple-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(168, 85, 247, 0.5), 0 0 16px rgba(168, 85, 247, 0.3)',
            animation: 'glow-purple 2s ease-in-out infinite, wiggle 3s ease-in-out infinite'
          }}>🥔 class Potato &#123; constructor() &#123; this.cuteness = 100; &#125; &#125;</span>
          <span className="text-yellow-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(234, 179, 8, 0.5), 0 0 16px rgba(234, 179, 8, 0.3)',
            animation: 'glow-yellow 2s ease-in-out infinite 0.3s, wiggle 3s ease-in-out infinite 0.5s'
          }}>🎨 let art = new Canvas(&apos;colorful&apos;);</span>
          <span className="text-teal-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(20, 184, 166, 0.5), 0 0 16px rgba(20, 184, 166, 0.3)',
            animation: 'glow-teal 2s ease-in-out infinite 0.7s, wiggle 3s ease-in-out infinite 1s'
          }}>🚀 blockchain.deploy(happiness);</span>
        </div>
      </div>

      {/* 第三層程式碼流 */}
      <div className="absolute top-56 left-0 w-full animate-pulse delay-700">
        <div className="flex space-x-10 animate-bounce delay-1000">
          <span className="text-red-500 font-mono text-sm font-bold transform hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 6px rgba(239, 68, 68, 0.5), 0 0 12px rgba(239, 68, 68, 0.3)',
            animation: 'dance 4s ease-in-out infinite, glow-red 1.5s ease-in-out infinite'
          }}>💫 while(true) &#123; spreadJoy(); &#125;</span>
          <span className="text-indigo-500 font-mono text-sm font-bold transform hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 6px rgba(99, 102, 241, 0.5), 0 0 12px rgba(99, 102, 241, 0.3)',
            animation: 'dance 4s ease-in-out infinite 0.5s, glow-indigo 1.5s ease-in-out infinite 0.3s'
          }}>🌟 const magic = () =&gt; dreams.comeTrue();</span>
          <span className="text-orange-500 font-mono text-sm font-bold transform hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 6px rgba(249, 115, 22, 0.5), 0 0 12px rgba(249, 115, 22, 0.3)',
            animation: 'dance 4s ease-in-out infinite 1s, glow-orange 1.5s ease-in-out infinite 0.7s'
          }}>🎮 game.start(&#123; fun: true, colors: &apos;all&apos; &#125;);</span>
        </div>
      </div>

      {/* 第四層程式碼流 */}
      <div className="absolute bottom-40 right-0 w-full animate-pulse delay-1200">
        <div className="flex space-x-7 animate-bounce delay-300">
          <span className="text-cyan-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 10px rgba(6, 182, 212, 0.5), 0 0 20px rgba(6, 182, 212, 0.3)',
            animation: 'float-text 3s ease-in-out infinite, sparkle 2s ease-in-out infinite'
          }}>🌸 for(let i = 0; i &lt; infinity; i++) &#123; smile(); &#125;</span>
          <span className="text-rose-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 10px rgba(244, 63, 94, 0.5), 0 0 20px rgba(244, 63, 94, 0.3)',
            animation: 'float-text 3s ease-in-out infinite 0.5s, sparkle 2s ease-in-out infinite 0.3s'
          }}>💝 export default Love;</span>
          <span className="text-lime-500 font-mono text-lg font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 10px rgba(132, 204, 22, 0.5), 0 0 20px rgba(132, 204, 22, 0.3)',
            animation: 'float-text 3s ease-in-out infinite 1s, sparkle 2s ease-in-out infinite 0.7s'
          }}>🎪 async function party() &#123; await celebrate(); &#125;</span>
        </div>
      </div>

      {/* 第五層程式碼流 */}
      <div className="absolute bottom-20 left-0 w-full animate-pulse delay-1500">
        <div className="flex space-x-9 animate-bounce delay-700">
          <span className="text-violet-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(139, 92, 246, 0.5), 0 0 16px rgba(139, 92, 246, 0.3)',
            animation: 'bounce-text 2.5s ease-in-out infinite, rainbow-glow 3s ease-in-out infinite'
          }}>🦄 const unicorn = new Magical(&#123; sparkles: true &#125;);</span>
          <span className="text-emerald-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(16, 185, 129, 0.5), 0 0 16px rgba(16, 185, 129, 0.3)',
            animation: 'bounce-text 2.5s ease-in-out infinite 0.3s, rainbow-glow 3s ease-in-out infinite 0.5s'
          }}>🍀 try &#123; makeWish(); &#125; catch(e) &#123; tryAgain(); &#125;</span>
          <span className="text-amber-500 font-mono text-base font-bold transform hover:scale-110 transition-all duration-300" style={{
            textShadow: '0 0 8px rgba(245, 158, 11, 0.5), 0 0 16px rgba(245, 158, 11, 0.3)',
            animation: 'bounce-text 2.5s ease-in-out infinite 0.7s, rainbow-glow 3s ease-in-out infinite 1s'
          }}>🎭 render(&lt;Happiness /&gt;);</span>
        </div>
      </div>

      {/* 垂直流動程式碼 */}
      <div className="absolute left-10 top-0 h-full">
        <div className="flex flex-col space-y-4 animate-pulse">
          <span className="text-pink-400 font-mono text-xs font-bold transform rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(244, 114, 182, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite'
          }}>🌺 &#123;color: &apos;rainbow&apos;&#125;</span>
          <span className="text-blue-400 font-mono text-xs font-bold transform rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(96, 165, 250, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 0.5s'
          }}>💎 .sparkle()</span>
          <span className="text-green-400 font-mono text-xs font-bold transform rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(74, 222, 128, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 1s'
          }}>🌱 grow()</span>
          <span className="text-purple-400 font-mono text-xs font-bold transform rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(196, 181, 253, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 1.5s'
          }}>🎨 paint()</span>
        </div>
      </div>

      <div className="absolute right-10 top-0 h-full">
        <div className="flex flex-col space-y-6 animate-pulse delay-500">
          <span className="text-yellow-400 font-mono text-xs font-bold transform -rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(251, 191, 36, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 0.3s'
          }}>☀️ shine()</span>
          <span className="text-red-400 font-mono text-xs font-bold transform -rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(248, 113, 113, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 0.8s'
          }}>❤️ love++</span>
          <span className="text-teal-400 font-mono text-xs font-bold transform -rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(45, 212, 191, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 1.3s'
          }}>🌊 flow()</span>
          <span className="text-orange-400 font-mono text-xs font-bold transform -rotate-90 hover:scale-125 transition-all duration-300" style={{
            textShadow: '0 0 5px rgba(251, 146, 60, 0.5)',
            animation: 'vertical-float 4s ease-in-out infinite 1.8s'
          }}>🔥 ignite()</span>
        </div>
      </div>

      {/* 浮動程式碼泡泡 */}
      <div className="absolute top-20 left-1/4 animate-bounce delay-200">
        <div className="bg-pink-200/50 rounded-full px-3 py-1 text-pink-600 font-mono text-xs font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 5px rgba(219, 39, 119, 0.5)',
          animation: 'bubble-float 3s ease-in-out infinite, glow-pink 2s ease-in-out infinite'
        }}>
          💕 cute++
        </div>
      </div>

      <div className="absolute top-60 right-1/4 animate-bounce delay-800">
        <div className="bg-blue-200/50 rounded-full px-3 py-1 text-blue-600 font-mono text-xs font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 5px rgba(37, 99, 235, 0.5)',
          animation: 'bubble-float 3s ease-in-out infinite 0.5s, glow-blue 2s ease-in-out infinite 0.3s'
        }}>
          🌊 flow()
        </div>
      </div>

      <div className="absolute bottom-32 left-1/3 animate-bounce delay-1100">
        <div className="bg-green-200/50 rounded-full px-3 py-1 text-green-600 font-mono text-xs font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 5px rgba(22, 163, 74, 0.5)',
          animation: 'bubble-float 3s ease-in-out infinite 1s, glow-green 2s ease-in-out infinite 0.7s'
        }}>
          🌱 bloom()
        </div>
      </div>

      <div className="absolute bottom-60 right-1/3 animate-bounce delay-400">
        <div className="bg-purple-200/50 rounded-full px-3 py-1 text-purple-600 font-mono text-xs font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 5px rgba(147, 51, 234, 0.5)',
          animation: 'bubble-float 3s ease-in-out infinite 0.7s, glow-purple 2s ease-in-out infinite 1s'
        }}>
          ✨ magic()
        </div>
      </div>

      {/* 對角線程式碼 */}
      <div className="absolute top-1/4 left-1/4 transform rotate-45 animate-pulse delay-600">
        <span className="text-indigo-500 font-mono text-sm font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 8px rgba(99, 102, 241, 0.5)',
          animation: 'diagonal-dance 5s ease-in-out infinite'
        }}>🎪 celebrate(joy)</span>
      </div>

      <div className="absolute top-3/4 right-1/4 transform -rotate-45 animate-pulse delay-900">
        <span className="text-rose-500 font-mono text-sm font-bold hover:scale-125 transition-all duration-300" style={{
          textShadow: '0 0 8px rgba(244, 63, 94, 0.5)',
          animation: 'diagonal-dance 5s ease-in-out infinite 1s'
        }}>🎈 float(dreams)</span>
      </div>
    </div>
  );
}
